const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let accessToken: string | null = null;
let refreshHandler: (() => Promise<boolean>) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function setRefreshHandler(handler: (() => Promise<boolean>) | null): void {
  refreshHandler = handler;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Sonda de conectividad real: `navigator.onLine` no detecta un backend caído. */
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/health`, {
      cache: 'no-store',
      headers: { accept: 'application/json' },
    });
    return response.ok;
  } catch {
    return false;
  }
}

interface RequestOptions extends RequestInit {
  retryAuth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { retryAuth = true, headers, ...init } = options;
  const finalHeaders = new Headers(headers);
  if (init.body && !finalHeaders.has('content-type')) {
    finalHeaders.set('content-type', 'application/json');
  }
  if (accessToken) finalHeaders.set('authorization', `Bearer ${accessToken}`);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: 'include',
      headers: finalHeaders,
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Sin conexión con el servidor');
  }

  if (
    response.status === 401 &&
    retryAuth &&
    refreshHandler &&
    !path.startsWith('/api/auth/')
  ) {
    const refreshed = await refreshHandler();
    if (refreshed) return request<T>(path, { ...options, retryAuth: false });
  }

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; details?: unknown } })
      ?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'API_ERROR',
      error?.message ?? `Error ${response.status}`,
      error?.details,
    );
  }

  return payload as T;
}

/** Descarga un binario autenticado (PDF, XLSX o XML) y renueva la sesión una vez si expiró. */
export async function descargarArchivo(path: string): Promise<Blob> {
  const run = async (retryAuth: boolean): Promise<Response> => {
    const headers = new Headers();
    if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
    const response = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      headers,
    });
    if (response.status === 401 && retryAuth && refreshHandler) {
      const refreshed = await refreshHandler();
      if (refreshed) return run(false);
    }
    return response;
  };

  let response: Response;
  try {
    response = await run(true);
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Sin conexión con el servidor');
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let message = `Error ${response.status}`;
    try {
      const payload = JSON.parse(text) as { error?: { message?: string; code?: string } };
      message = payload.error?.message ?? message;
    } catch {
      // el cuerpo no es JSON; se conserva el mensaje genérico
    }
    throw new ApiError(response.status, 'DOWNLOAD_ERROR', message);
  }
  return response.blob();
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const api = {
  login: (identifier: string, password: string) =>
    request<{ data: import('./types').AuthPayload }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password }),
      retryAuth: false,
    }),

  refresh: () =>
    request<{ data: import('./types').AuthPayload }>('/api/auth/refresh', {
      method: 'POST',
      retryAuth: false,
    }),

  logout: () =>
    request<{ data: { ok: boolean } }>('/api/auth/logout', {
      method: 'POST',
      retryAuth: false,
    }),

  dashboard: () =>
    request<{ data: import('./types').DashboardSummary }>('/api/dashboard/summary'),

  bootstrap: () =>
    request<{ data: import('./types').BootstrapData }>('/api/sync/bootstrap'),

  push: (payload: {
    deviceId: string;
    mutations: import('./types').SyncMutation[];
  }) =>
    request<{ data: import('./types').SyncPushResponse }>('/api/sync/push', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  pedidos: {
    list: (params: {
      estado?: string;
      desde?: string;
      hasta?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) => request<import('./types').ListResponse<import('./types').Pedido>>(
      `/api/pedidos${qs(params)}`,
    ),
    create: (payload: Record<string, unknown>) =>
      request<{ data: import('./types').Pedido }>('/api/pedidos', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    detail: (id: string) =>
      request<{ data: import('./types').Pedido & { items: import('./types').PedidoItem[] } }>(
        `/api/pedidos/${id}`,
      ),
    cambiarEstado: (id: string, estado: string, motivo?: string) =>
      request<{ data: { id: string; estado: string } }>(`/api/pedidos/${id}/estado`, {
        method: 'PATCH',
        body: JSON.stringify({ estado, ...(motivo ? { motivo } : {}) }),
      }),
  },

  cotizaciones: {
    list: (params: { search?: string; page?: number; limit?: number }) =>
      request<import('./types').ListResponse<import('./types').Cotizacion>>(
        `/api/cotizaciones${qs(params)}`,
      ),
    create: (payload: Record<string, unknown>) =>
      request<{ data: import('./types').Cotizacion }>('/api/cotizaciones', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  },

  facturas: {
    list: (params: { estado?: string; search?: string; page?: number; limit?: number }) =>
      request<import('./types').ListResponse<import('./types').Factura>>(
        `/api/facturas${qs(params)}`,
      ),
  },

  clientes: {
    list: (params: { search?: string; page?: number; limit?: number }) =>
      request<import('./types').ListResponse<import('./types').Cliente>>(
        `/api/clientes${qs(params)}`,
      ),
    create: (payload: Record<string, unknown>) =>
      request<{ data: { id: string } }>('/api/clientes', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    resumen: (id: string) =>
      request<{ data: import('./types').ResumenCliente }>(`/api/clientes/${id}/resumen`),
    update: (id: string, payload: Record<string, unknown>) =>
      request<{ data: { id: string } }>(`/api/clientes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
  },

  productos: {
    list: (params: { search?: string; limit?: number } = {}) =>
      request<import('./types').ListResponse<import('./types').Producto>>(
        `/api/productos${qs(params)}`,
      ),
    create: (payload: Record<string, unknown>) =>
      request<{ data: { id: string } }>('/api/productos', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  },

  imprenta: {
    estado: () =>
      request<{
        data: {
          proveedor: string;
          configurado: boolean;
          baseUrl: string;
          ambiente: 'DEMO' | 'PRODUCCION';
          eventos24h: number;
          errores24h: number;
          ultimoEventoAt: string | null;
        };
      }>('/api/imprenta/estado'),
    probar: () =>
      request<{
        data: {
          ok: boolean;
          configurado: boolean;
          baseUrl: string;
          expiraEn?: string;
          mensaje: string;
        };
      }>('/api/imprenta/probar', { method: 'POST', body: JSON.stringify({}) }),
    list: (params: { estado?: string; search?: string; page?: number; limit?: number } = {}) =>
      request<{
        data: import('./types').ImprentaEvento[];
        meta: import('./types').PaginationMeta;
      }>(`/api/imprenta/eventos${qs(params)}`),
    detail: (id: string) =>
      request<{ data: import('./types').ImprentaEventoDetalle }>(
        `/api/imprenta/eventos/${id}`,
      ),
  },

  catalogos: {
    productos: () =>
      request<{ data: import('./types').CatalogosProducto }>('/api/catalogos/productos'),
    crearCategoria: (payload: { nombre: string; descripcion?: string }) =>
      request<{ data: { id: string; nombre: string } }>('/api/catalogos/categorias', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    crearUnidad: (payload: { codigo: string; nombre: string; permiteDecimales: boolean }) =>
      request<{ data: { id: string; codigo: string; nombre: string } }>('/api/catalogos/unidades', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    estadoCategoria: (id: string, activo: boolean) =>
      request<{ data: { id: string; activo: boolean } }>(`/api/catalogos/categorias/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ activo }),
      }),
    estadoUnidad: (id: string, activo: boolean) =>
      request<{ data: { id: string; activo: boolean } }>(`/api/catalogos/unidades/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ activo }),
      }),
  },

  sucursales: {
    list: () =>
      request<{ data: import('./types').Sucursal[] }>('/api/sucursales'),
    create: (payload: { codigo: string; nombre: string; direccion?: string }) =>
      request<{ data: { id: string } }>('/api/sucursales', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  },

  tasas: {
    list: () => request<{ data: import('./types').Tasa[] }>('/api/tasas'),
    dia: (fecha: string) =>
      request<{ data: import('./types').Tasa[]; fecha: string }>(
        `/api/tasas/dia${qs({ fecha })}`,
      ),
    crear: (payload: { moneda: string; tasa: number; fuente?: string }) =>
      request<{ data: { id: string } }>('/api/tasas', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    sync: () =>
      request<{
        data: { fuente: string; recibidas: number; insertadas: number; rates: unknown[] };
      }>('/api/tasas/sync', { method: 'POST' }),
  },

  integraciones: () =>
    request<{ data: { generatedAt: string; integrations: import('./types').IntegracionEstado[] } }>(
      '/api/integraciones/estado',
    ),

  finanzas: {
    facturas: {
      list: (params: {
        estado?: string;
        desde?: string;
        hasta?: string;
        search?: string;
        page?: number;
        limit?: number;
      }) =>
        request<import('./types').ListResponse<import('./types').Factura>>(
          `/api/facturas${qs(params)}`,
        ),
      pendientes: (clienteId?: string) =>
        request<{ data: import('./types').PendingInvoice[] }>(
          `/api/facturas/pendientes${qs({ clienteId })}`,
        ),
      detail: (id: string) =>
        request<{ data: import('./types').FacturaDetalle }>(`/api/facturas/${id}`),
      crear: (payload: Record<string, unknown>) =>
        request<{ data: import('./types').Factura }>('/api/facturas', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      anular: (id: string, motivo: string) =>
        request<{ data: { id: string; estado: string; reversoId: string | null } }>(
          `/api/facturas/${id}/anular`,
          { method: 'POST', body: JSON.stringify({ motivo }) },
        ),
      fiscal: (id: string, payload: { modo?: 'LOCAL' | 'TFHKA'; force?: boolean } = {}) =>
        request<{
          data: {
            numeroControl: string | null;
            estado: string;
            proveedor: string | null;
            uuid: string | null;
            hash: string | null;
            emitidaAt: string | null;
            xml?: string | null;
          };
        }>(`/api/facturas/${id}/fiscal`, {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      fiscalResumen: (id: string) =>
        request<{
          data: {
            numeroControl: string | null;
            estado: string;
            proveedor: string | null;
            uuid: string | null;
            hash: string | null;
            emitidaAt: string | null;
          };
        }>(`/api/facturas/${id}/fiscal`),
    },
    notas: {
      list: (params: {
        facturaId?: string;
        clienteId?: string;
        tipo?: 'CREDITO' | 'DEBITO';
        desde?: string;
        hasta?: string;
        limit?: number;
      } = {}) =>
        request<{ data: import('./types').NotaFiscal[] }>(`/api/notas${qs(params)}`),
      detail: (id: string) =>
        request<{ data: import('./types').NotaFiscal }>(`/api/notas/${id}`),
      crear: (payload: Record<string, unknown>) =>
        request<{
          data: import('./types').NotaFiscal & {
            fiscal?: {
              numeroControl: string | null;
              estado: string;
              proveedor: string | null;
              url: string | null;
            } | null;
            fiscalError?: string | null;
          };
        }>('/api/notas', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      anular: (id: string, motivo: string) =>
        request<{ data: { id: string; estado: string; reversoId: string | null } }>(
          `/api/notas/${id}/anular`,
          { method: 'POST', body: JSON.stringify({ motivo }) },
        ),
      fiscal: (id: string) =>
        request<{
          data: {
            numeroControl: string | null;
            estado: string;
            proveedor: string | null;
            uuid: string | null;
            hash: string | null;
            emitidaAt: string | null;
            xml?: string | null;
          };
        }>(`/api/notas/${id}/fiscal`, { method: 'POST', body: JSON.stringify({}) }),
    },
    compras: {
      list: (params: {
        estado?: string;
        desde?: string;
        hasta?: string;
        search?: string;
        page?: number;
        limit?: number;
      }) =>
        request<import('./types').ListResponse<import('./types').Compra>>(
          `/api/compras${qs(params)}`,
        ),
      pendientes: (proveedorId?: string) =>
        request<{
          data: Array<{
            id: string;
            numeroInterno: string;
            numeroFactura: string;
            fechaEmision: string;
            baseUsd: number;
            baseVes?: number;
            tasa?: number;
            totalUsd: number;
            impuestoUsd?: number;
            saldoUsd: number;
            saldoVes?: number;
            islrPct?: number;
            retencionIvaPct?: number;
            retencionIslrPrevistaUsd?: number;
            retencionIvaPrevistaUsd?: number;
            estado: string;
            proveedorNombre: string;
            proveedorRif: string;
          }>;
        }>(`/api/compras/pendientes${qs({ proveedorId })}`),
      detail: (id: string) =>
        request<{ data: import('./types').CompraDetalle }>(`/api/compras/${id}`),
      crear: (payload: Record<string, unknown>) =>
        request<{ data: import('./types').Compra }>('/api/compras', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      pagos: () =>
        request<{ data: import('./types').PagoProveedor[] }>('/api/compras/pagos'),
      notas: (params: {
        tipo?: 'CREDITO' | 'DEBITO';
        desde?: string;
        hasta?: string;
        search?: string;
      } = {}) =>
        request<{
          data: import('./types').CompraNotaResumen[];
          totales: { documentos: number; totalUsd: number; totalVes: number };
        }>(`/api/compras/notas${qs(params)}`),
      crearNota: (id: string, payload: Record<string, unknown>) =>
        request<{ data: import('./types').CompraNota }>(`/api/compras/${id}/notas`, {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      registrarPago: (payload: Record<string, unknown>) =>
        request<{ data: Record<string, unknown> }>('/api/compras/pagos', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      anular: (id: string, motivo: string) =>
        request<{ id: string; estado: string; reversoId: string | null }>(
          `/api/compras/${id}/anular`,
          { method: 'POST', body: JSON.stringify({ motivo }) },
        ),
      anularPago: (id: string, motivo: string) =>
        request<{ id: string; estado: string; reversoId: string | null; compras: number }>(
          `/api/compras/pagos/${id}/anular`,
          { method: 'POST', body: JSON.stringify({ motivo }) },
        ),
    },
    cobranzas: {
      list: (params: {
        desde?: string;
        hasta?: string;
        search?: string;
        estado?: string;
        page?: number;
        limit?: number;
      }) =>
        request<import('./types').ListResponse<import('./types').Cobranza>>(
          `/api/cobranzas${qs(params)}`,
        ),
      detail: (id: string) =>
        request<{ data: import('./types').Cobranza & { aplicaciones: unknown[] } }>(
          `/api/cobranzas/${id}`,
        ),
      crear: (payload: Record<string, unknown>) =>
        request<{ data: Record<string, unknown> }>('/api/cobranzas', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      anular: (id: string, motivo: string) =>
        request<{ data: Record<string, unknown> }>(`/api/cobranzas/${id}/anular`, {
          method: 'POST',
          body: JSON.stringify({ motivo }),
        }),
    },
    proveedores: {
      list: (params: { search?: string; page?: number; limit?: number } = {}) =>
        request<import('./types').ListResponse<import('./types').Proveedor>>(
          `/api/proveedores${qs(params)}`,
        ),
      crear: (payload: Record<string, unknown>) =>
        request<{ data: { id: string } }>('/api/proveedores', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      resumen: (id: string) =>
        request<{ data: import('./types').ResumenProveedor }>(
          `/api/proveedores/${id}/resumen`,
        ),
    },
    inventario: {
      list: (params: { search?: string; page?: number; limit?: number } = {}) =>
        request<import('./types').ListResponse<import('./types').InventarioItem>>(
          `/api/inventario${qs(params)}`,
        ),
      bajoStock: () =>
        request<{ data: import('./types').InventarioItem[] }>('/api/inventario/bajo-stock'),
      kardex: (id: string) =>
        request<{
          data: {
            producto: import('./types').InventarioItem;
            movimientos: import('./types').KardexMovimiento[];
          };
        }>(`/api/inventario/${id}/kardex`),
      ajuste: (payload: { productoId: string; cantidad: number; motivo: string }) =>
        request<{ data: Record<string, unknown> }>('/api/inventario/ajustes', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
    },
    banca: {
      cuentas: () =>
        request<{ data: import('./types').CuentaBancaria[] }>('/api/banca/cuentas'),
      crearCuenta: (payload: Record<string, unknown>) =>
        request<{ data: { id: string } }>('/api/banca/cuentas', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      movimientos: (params: {
        cuentaId?: string;
        desde?: string;
        hasta?: string;
        page?: number;
        limit?: number;
      } = {}) =>
        request<import('./types').ListResponse<import('./types').MovimientoBancario>>(
          `/api/banca/movimientos${qs(params)}`,
        ),
      transaccion: (payload: Record<string, unknown>) =>
        request<{ data: Record<string, unknown> }>('/api/banca/transacciones', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      conciliar: (payload: Record<string, unknown>) =>
        request<{ data: Record<string, unknown> }>('/api/banca/conciliaciones', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
    },
    contabilidad: {
      cuentas: () =>
        request<{ data: import('./types').CuentaContable[] }>('/api/contabilidad/cuentas'),
      crearCuenta: (payload: Record<string, unknown>) =>
        request<{ data: { id: string } }>('/api/contabilidad/cuentas', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      asientos: (params: {
        desde?: string;
        hasta?: string;
        origen?: string;
        estado?: string;
        page?: number;
        limit?: number;
      } = {}) =>
        request<import('./types').ListResponse<import('./types').AsientoResumen>>(
          `/api/contabilidad/asientos${qs(params)}`,
        ),
      asiento: (id: string) =>
        request<{ data: import('./types').AsientoDetalle }>(
          `/api/contabilidad/asientos/${id}`,
        ),
      crearAsiento: (payload: Record<string, unknown>) =>
        request<{ data: import('./types').AsientoResumen }>('/api/contabilidad/asientos', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      anularAsiento: (id: string, motivo: string) =>
        request<{ data: Record<string, unknown> }>(
          `/api/contabilidad/asientos/${id}/anular`,
          { method: 'POST', body: JSON.stringify({ motivo }) },
        ),
      balance: (params: { desde?: string; hasta?: string } = {}) =>
        request<{
          data: import('./types').BalanceRow[];
          totales: {
            debeUsd: number;
            haberUsd: number;
            debeVes: number;
            haberVes: number;
            cuadrado: boolean;
          };
        }>(`/api/contabilidad/balance${qs(params)}`),
      mayor: (params: { cuentaId: string; desde?: string; hasta?: string }) =>
        request<{
          data: {
            cuenta: import('./types').CuentaContable;
            saldoInicialUsd: number;
            movimientos: Array<{
              asientoId: string;
              numero: number;
              fecha: string;
              asientoDescripcion: string;
              descripcion: string | null;
              debeUsd: number;
              haberUsd: number;
              debeVes: number;
              haberVes: number;
              saldoUsd: number;
              saldoVes: number;
            }>;
            saldoFinalUsd: number;
            saldoFinalVes: number;
          };
        }>(`/api/contabilidad/mayor${qs(params)}`),
      periodos: () =>
        request<{ data: import('./types').PeriodoFiscal[] }>('/api/contabilidad/periodos'),
      cerrarPeriodo: (id: string) =>
        request<{ data: { id: string; estado: string } }>(
          `/api/contabilidad/periodos/${id}/cerrar`,
          { method: 'POST' },
        ),
      mapeos: () =>
        request<{ data: import('./types').MapeoCuenta[] }>('/api/contabilidad/mapeos'),
      actualizarMapeo: (payload: { clave: string; cuentaId: string }) =>
        request<{ data: { clave: string; cuentaId: string } }>(
          '/api/contabilidad/mapeos',
          { method: 'POST', body: JSON.stringify(payload) },
        ),
      correlativos: () =>
        request<{ data: import('./types').Correlativo[] }>('/api/contabilidad/correlativos'),
      actualizarCorrelativo: (payload: { siguienteNumero: number; password: string }) =>
        request<{ data: import('./types').Correlativo }>('/api/contabilidad/correlativos', {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }),
    },
    proyectos: {
      list: () =>
        request<{ data: import('./types').Proyecto[] }>('/api/proyectos'),
      detail: (id: string) =>
        request<{ data: import('./types').ProyectoDetalle }>(`/api/proyectos/${id}`),
      crear: (payload: Record<string, unknown>) =>
        request<{ data: { id: string } }>('/api/proyectos', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
    },
    retenciones: {
      list: (params: {
        desde?: string;
        hasta?: string;
        origen?: 'CLIENTE' | 'PROVEEDOR';
        incluirAnuladas?: boolean;
      } = {}) =>
        request<{
          data: import('./types').Retencion[];
          totales: Record<string, number>;
          desde: string;
          hasta: string;
        }>(`/api/retenciones${qs(params)}`),
      anular: (origen: 'CLIENTE' | 'PROVEEDOR', id: string, motivo: string) =>
        request<{
          data: {
            asientoNumero: number;
            saldoDocumento: number;
            tfhkaAplicada: boolean;
            aviso: string | null;
          };
        }>(`/api/retenciones/${origen}/${id}/anular`, {
          method: 'POST',
          body: JSON.stringify({ motivo }),
        }),
      enviar: (id: string) =>
        request<{
          data: {
            estado: 'APLICADA' | 'PENDIENTE' | 'NO_APLICA' | 'ERROR';
            mensaje: string | null;
          };
        }>(`/api/retenciones/cliente/${id}/enviar`, { method: 'POST', body: JSON.stringify({}) }),
    },
    reportes: {
      libroVentas: (params: { desde?: string; hasta?: string }) =>
        request<{
          data: unknown[];
          totales: Record<string, number>;
          desde: string;
          hasta: string;
        }>(`/api/reportes/libro-ventas${qs(params)}`),
      libroCompras: (params: { desde?: string; hasta?: string }) =>
        request<{
          data: unknown[];
          totales: Record<string, number>;
          desde: string;
          hasta: string;
        }>(`/api/reportes/libro-compras${qs(params)}`),
      retenciones: (params: { desde?: string; hasta?: string }) =>
        request<{
          data: unknown[];
          totales: Record<string, number>;
          desde: string;
          hasta: string;
        }>(`/api/reportes/retenciones-islr${qs(params)}`),
      retencionesVentas: (params: { desde?: string; hasta?: string }) =>
        request<{
          data: unknown[];
          totales: Record<string, number>;
          desde: string;
          hasta: string;
        }>(`/api/reportes/retenciones-ventas${qs(params)}`),
      balanceComprobacion: (params: { desde?: string; hasta?: string }) =>
        request<{
          data: unknown[];
          totales: Record<string, number>;
          desde: string;
          hasta: string;
        }>(`/api/reportes/balance-comprobacion${qs(params)}`),
      estadoResultados: (params: { desde?: string; hasta?: string }) =>
        request<{
          data: unknown[];
          totales: Record<string, number>;
          desde: string;
          hasta: string;
        }>(`/api/reportes/estado-resultados${qs(params)}`),
    },
    dashboardFinanciero: () =>
      request<{ data: import('./types').DashboardFinanciero }>('/api/dashboard/financiero'),
    usuarios: {
      list: (params: { search?: string } = {}) =>
        request<{ data: import('./types').UsuarioAdmin[] }>(
          `/api/usuarios${qs(params)}`,
        ),
      crear: (payload: Record<string, unknown>) =>
        request<{ data: { id: string } }>('/api/usuarios', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      actualizar: (id: string, payload: Record<string, unknown>) =>
        request<{ data: import('./types').UsuarioAdmin }>(`/api/usuarios/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }),
      resetPassword: (id: string, password: string) =>
        request<{ data: { id: string; ok: boolean } }>(`/api/usuarios/${id}/password`, {
          method: 'POST',
          body: JSON.stringify({ password }),
        }),
    },
  },
};

/** Identificador estable del dispositivo para los lotes de sincronización. */
export function deviceId(): string {
  const key = 'gravenca.deviceId';
  const existing = window.localStorage.getItem(key);
  if (existing) return existing;
  const created = `web-${crypto.randomUUID()}`;
  window.localStorage.setItem(key, created);
  return created;
}
